/**
 * InstallBanner Component
 * Shows installation prompt/instructions for all platforms:
 * - Chrome/Edge: Shows install button that triggers native prompt
 * - Safari iOS: Shows instructions for Add to Home Screen
 * - Safari Mac: Shows instructions for Add to Dock
 */

import { useState, useEffect } from 'react'
import { X, Share, Plus, Download } from 'lucide-react'

type Platform = 'ios' | 'mac' | 'chromium' | null

// Store the beforeinstallprompt event globally
let deferredPrompt: BeforeInstallPromptEvent | null = null

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent

  // Check if already installed as PWA
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return null
  }

  // Check if Safari (not Chrome/Firefox/etc disguised as Safari)
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua)

  if (isSafari) {
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    if (isIOS) {
      return 'ios'
    }

    // Detect Mac
    const isMac = /Macintosh|MacIntel/.test(ua)
    if (isMac) {
      return 'mac'
    }
  }

  // Chrome/Edge on desktop or Android
  if (/Chrome|Edg/.test(ua)) {
    return 'chromium'
  }

  return null
}

export function InstallBanner() {
  const [platform, setPlatform] = useState<Platform>(null)
  const [dismissed, setDismissed] = useState(true)
  const [canInstall, setCanInstall] = useState(false)

  useEffect(() => {
    // Check if previously dismissed
    const wasDismissed = localStorage.getItem('install-banner-dismissed')
    if (wasDismissed) {
      return
    }

    const detectedPlatform = detectPlatform()

    if (detectedPlatform === 'chromium') {
      // For Chromium browsers, wait for beforeinstallprompt event
      const handleBeforeInstallPrompt = (e: Event) => {
        e.preventDefault()
        deferredPrompt = e as BeforeInstallPromptEvent
        setPlatform('chromium')
        setCanInstall(true)
        setDismissed(false)
      }

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

      // Check if we already have a deferred prompt
      if (deferredPrompt) {
        setPlatform('chromium')
        setCanInstall(true)
        setDismissed(false)
      }

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      }
    } else if (detectedPlatform) {
      setPlatform(detectedPlatform)
      setDismissed(false)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    // Show the install prompt
    await deferredPrompt.prompt()

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setDismissed(true)
      localStorage.setItem('install-banner-dismissed', 'true')
    }

    // Clear the deferred prompt
    deferredPrompt = null
    setCanInstall(false)
  }

  const handleDismiss = () => {
    setDismissed(true)
    localStorage.setItem('install-banner-dismissed', 'true')
  }

  if (dismissed || !platform) {
    return null
  }

  // For Chromium, only show if we can actually install
  if (platform === 'chromium' && !canInstall) {
    return null
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 p-4 bg-white dark:bg-nature-800 border-b border-nature-200 dark:border-nature-700 shadow-lg">
      <div className="max-w-lg mx-auto">
        <div className="flex items-start gap-3">
          {/* App icon */}
          <img
            src="/android-chrome-192x192.png"
            alt="App icon"
            className="w-12 h-12 rounded-xl flex-shrink-0"
          />

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-nature-900 dark:text-nature-100">
              Installera appen
            </h3>

            {platform === 'ios' && (
              <p className="text-sm text-nature-600 dark:text-nature-400 mt-1">
                Tryck på{' '}
                <Share className="inline h-4 w-4 text-primary-500" />{' '}
                och sedan{' '}
                <span className="inline-flex items-center gap-1">
                  <Plus className="inline h-4 w-4" />
                  <span className="font-medium">Lägg till på hemskärmen</span>
                </span>
              </p>
            )}

            {platform === 'mac' && (
              <p className="text-sm text-nature-600 dark:text-nature-400 mt-1">
                Klicka på{' '}
                <span className="font-medium">Arkiv</span>
                {' → '}
                <span className="font-medium">Lägg till i Dock</span>
              </p>
            )}

            {platform === 'chromium' && (
              <button
                onClick={handleInstallClick}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Download className="h-4 w-4" />
                Installera
              </button>
            )}
          </div>

          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 flex-shrink-0"
            aria-label="Stäng"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
